import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";

import Widget from "../../components/Widget/index";

import React, { useEffect } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from "react-redux";
import { fetchMerchantAccounts } from '../../redux/actions/Data';
import { Col, Row } from "antd";

import { fetchGeneralTotal, fetchGeneralTotalCumulative, fetchMerchantTotal } from "../../redux/actions/Aggregate";
import { fetchTransactionSummary } from "../../redux/actions/Transactions";
import { dateAndTimeRangeQueriesArray } from "../../util/lib";
import { TransactionTypes } from "../../constants/System";
import { TIMEZONE } from "../../constants/ThemeSetting";


const QueriesCard = ({ merchantId }) => {
  const dispatch = useDispatch();
  const { data: transactionSummaryThisMonth } = useSelector(({ transactions }) => transactions.transactionSummaryThisMonth);
  const { data: transactionSummaryLastMonth } = useSelector(({ transactions }) => transactions.transactionSummaryLastMonth);
  const { data: generalDailyThisMonth } = useSelector(({ aggregate }) => aggregate.generalDailyThisMonth);
  const { data: merchantDailyThisMonth } = useSelector(({ aggregate }) => aggregate.merchantDailyThisMonth);
  const { data: merchantAccounts, loading: loadingMerchants } = useSelector(({ data }) => data.merchantAccounts);


  // create a new date range for the previous month
  const startThisMonthDate = moment().startOf('month').format('YYYY-MM-DD');
  const endThisMonthDate = moment().endOf('month').format('YYYY-MM-DD');
  const startLastMonthDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  const endLastMonthDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

  const transactionType = TransactionTypes.COLLECTION;

  useEffect(() => {
    if (!merchantId) {
      dispatch(fetchMerchantAccounts());
    } else {
      const queries = [
        `merchant_id={"$in": ["${merchantId}"]}`,
        `transaction_type=${transactionType}`,
        ...dateAndTimeRangeQueriesArray(startThisMonthDate, endThisMonthDate, TIMEZONE),
      ];
      const params = queries.join('&');
      dispatch(fetchTransactionSummary(params, transactionType, 'this_month'));

      const queriesPreviousMonth = [
        `merchant_id={"$in": ["${merchantId}"]}`,
        `transaction_type=${transactionType}`,
        ...dateAndTimeRangeQueriesArray(startLastMonthDate, endLastMonthDate, TIMEZONE),
      ];
      const paramsPrev = queriesPreviousMonth.join('&');
      dispatch(fetchTransactionSummary(paramsPrev, transactionType, 'last_month'));

      dispatch(fetchMerchantTotal(
        merchantId,
        'CARD',
        "ALL",
        'COMPLETED',
        'daily',
        'this_month',
        `start=${moment(startThisMonthDate).format('YYYY-MM-DD')}&end=${moment(endThisMonthDate).format('YYYY-MM-DD')}`));
    }
  }, [dispatch, merchantId]);


  useEffect(() => {
    if (!merchantId && merchantAccounts && merchantAccounts.length > 0) {
      const merchantIds = `merchant_id={"$in": [${merchantAccounts.map((account) => `"${account._id}"`).join(',')}]}`;

      const queriesGeneral = [
        merchantIds,
        `start=${moment(startThisMonthDate).format('YYYY-MM-DD')}`,
        `end=${moment(endThisMonthDate).format('YYYY-MM-DD')}`
      ];
      const paramsGeneral = queriesGeneral.join('&');

      dispatch(fetchGeneralTotal(
        'CARD',
        "ALL",
        'COMPLETED',
        'daily',
        'this_month',
        paramsGeneral
      ));

      const queries = [
        merchantIds,
        `transaction_type=${transactionType}`,
        ...dateAndTimeRangeQueriesArray(startThisMonthDate, endThisMonthDate, TIMEZONE),
      ];
      const params = queries.join('&');
      dispatch(fetchTransactionSummary(params, transactionType, 'this_month'));

      const queriesPreviousMonth = [
        merchantIds,
        `transaction_type=${transactionType}`,
        ...dateAndTimeRangeQueriesArray(startLastMonthDate, endLastMonthDate, TIMEZONE),
      ];
      const paramsPrev = queriesPreviousMonth.join('&');
      dispatch(fetchTransactionSummary(paramsPrev, transactionType, 'last_month'));
    }

  }, [merchantAccounts]);

  const data = merchantId ? merchantDailyThisMonth : generalDailyThisMonth;

  return (
    <Widget>
      <div className="gx-d-flex">
        <p className="gx-text-uppercase gx-chart-title">Daily Monthly Volume</p>
        <p className="gx-ml-auto gx-text-primary">
          {
            // calculate the percentage change based on transactionSummaryThisMonth.total_count and transactionSummaryLastMonth.total_count
            transactionSummaryThisMonth.total_count && transactionSummaryLastMonth.total_count && transactionSummaryLastMonth.total_count !== 0
              ? ((transactionSummaryThisMonth.total_count - transactionSummaryLastMonth.total_count) / transactionSummaryLastMonth.total_count * 100).toFixed(0)
              : 0
          }% better than last month
          <i className={`icon icon-menu-${transactionSummaryThisMonth.total_count > transactionSummaryLastMonth.total_count ? 'up' : "down"} gx-fs-sm`} /></p>
      </div>
      <Row>
        <Col lg={4} md={4} sm={24} xs={24} className="gx-pr-2">
          <div className="gx-pt-2">
            <h2 className="gx-fs-xxxl gx-font-weight-medium gx-mb-1 gx-text-black">{transactionSummaryThisMonth.total_count}</h2>
            <p className="gx-mb-2 gx-mb-md-0">Everything</p>
          </div>
        </Col>
        <Col lg={20} md={20} sm={20} xs={20}>
          <ResponsiveContainer width="100%" height={60}>
            <BarChart data={data}
              margin={{ top: 5, right: 5, left: 30, bottom: 5 }}>
              <Tooltip />
              <XAxis dataKey="timestamp" tickFormatter={timeStr => moment(timeStr).format('DD')} />
              <defs>
                <linearGradient id="color08" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="5%" stopColor="#23DFDC" stopOpacity={0.9} />
                  <stop offset="95%" stopColor="#63AEE4" stopOpacity={0.9} />
                </linearGradient>
              </defs>
              <Bar dataKey="total_amount" fill="url(#color08)" barSize={8} />
            </BarChart>
          </ResponsiveContainer>
        </Col>
      </Row>
    </Widget>
  );
};

export default QueriesCard;
